// @flow

import { type ReceiverState } from 'Common/auth/types';
import DvrEventListing from 'Common/models/DvrEventListing';
import type { Franchise } from 'Common/franchise/types';

export const DEFAULT_RECORD_FREQUENCY_OPTION_KEY: number = 0;

// Models
export type timerFrequencyKeys =
  | 'once'
  | 'onlyNew'
  | 'newAndReruns'
  | 'daily'
  | 'weekly'
  | 'mondayFriday';

export type timerSortOption = {|
  sortBy: string,
  orderBy: string,
|};

export type TimerFrequenciesT = {
  [key: timerFrequencyKeys]: number,
};

/*
 * Options 4 and 7 have been deleted due to DE8991 (https://rally1.rallydev.com/#/233447464096d/detail/defect/295609707988)
 * In case if  timer contain deleted key (4 - 'Single Episode', 7 - 'Dish Pass'), options will be overwritten:
 * 'Single Episode' = 'Once'
 * 'All Episodes' = 'New and Reruns'
 * 'Dish Pass' = 'Only New' (by default)
 */
export const RECORD_FREQUENCY_OPTIONS = {
  '0': 'Once',
  '1': 'Weekly',
  '2': 'Monday-Friday',
  '3': 'Daily',
  '5': 'New and Reruns',
  '6': 'Only New',
};

export const TIMER_FREQUENCIES: TimerFrequenciesT = {
  once: 0,
  weekly: 1,
  mondayFriday: 2,
  daily: 3,
  newAndReruns: 5,
  onlyNew: 6,
};

export const DEFAULT_RECORD_SETTINGS = {
  priority: 1, // highest priority. values are by DAny Android - ESRA communication.
  startEarly: 1,
  endLate: 3,
  maxRecordings: 20,
  keep: 0,
  frequency: 0,
};

export type Stream = {|
  channel: number,
  svc_name: string,
  channelPadded: number,
|};

export type Recording = {
  count: number,
  image: string,
  latest_recording: string,
  name: string,
  series_id: ?number,
  slug: string,
};

export type RecordingDetail = {|
  category: string,
  channel: number,
  description: string,
  duration: number,
  episode_num: number,
  es_content_type: string,
  es_egid: string,
  es_id: number,
  event_name: string,
  ext_rating: string,
  hd: boolean,
  id: string,
  new: boolean,
  network_affiliate: string,
  pgm_id: number,
  program_logo: string,
  program_logo_dish_url: string,
  ptat: boolean,
  pvr_attrib: number,
  rec_time: string,
  season_num: number,
  series_id: number,
  svc_name: string,
  timestamp: number,
  title: string,
  upid: string,
  usid: number,
  view_time_remaining: number,
  slug: string,
|};

export type Recording10ft = {|
  ...RecordingDetail,
  es_id_string: string,
  saved_ptat: number,
  content_id: number,
  rating: string,
  is_parental_locked: boolean,
  franchise: Franchise,
|};

type TimerTmPeriod = {|
  duration: number,
  start_time: string,
|};

export type NewTimer = {|
  channel?: number,
  priority?: number,
  frequency?: number,
  start_time: string,
  duration: number,
  minutes_early?: number,
  minutes_late?: number,
  upid: string,
  event_count?: number,
  timer_attributes: number,
  timer_type: number | string,
  creator: number,
  channel_name?: ?string,
  program_name: ?string,
  event_name?: string,
  receiver_id?: string,
  gtimer_id?: number,
  transcode?: number,
  name?: string,
  program_logo?: string,
|};

export type Images = {
  wide_poster_url: ?string,
  poster_url: ?string,
  thumbnail_url: ?string,
};

export type Timer = {|
  channel: number,
  channel_name?: string,
  channel_number?: ?number,
  channel_callsign?: ?string,
  creator: number,
  dtimer_id: number,
  event_count?: ?number,
  episode_num: number,
  es_content_type: string,
  es_episode_id: number,
  es_id: number,
  event_name: string,
  filter: string,
  frequency: ?number,
  gtimer_id: number,
  min_early: number,
  min_late: number,
  network_affiliate: string,
  output: number,
  padded: string | number,
  priority: number,
  program_logo: string,
  program_name?: ?string,
  name?: string,
  season_num: number,
  series_id?: number,
  skip_opt: number,
  timer_attributes: number,
  timer_type: number,
  title: string,
  tm_period: TimerTmPeriod,
  upid: string,
  transcode?: number,
  short_description?: string,
  long_description?: string,
  channel_image?: string,
  program_images?: Images,
|};

export type LocalTimer = {|
  channel: number,
  startDateTime: string,
|};

export type Offset = {|
  left: string,
  top: number,
|};

export type DiskInfo = {|
  free_hd_mins: number,
  free_sd_mins: number,
  total_hd_mins: number,
  total_sd_mins: number,
|};

export type ReceiverInfo = {|
  disk_info: DiskInfo,
  total_recordings: number,
|};

export type Filters = {|
  sort?: string,
  filter?: string,
  sortOrder?: string,
|};

export type ReceiverStatus = {| receiver_id: string, status: ReceiverState, timestamp: number |};

export type RecordingsState = {|
  content: Array<Recording>,
  error: boolean,
  fetching: boolean,
  filters: ?Filters,
|};

export type Recordings10ftState = {|
  content: Array<Recording10ft>,
  error: boolean,
  fetching: boolean,
|};

export type createTimerState = {|
  fetching: boolean,
  error: boolean,
  content: Array<Timer>,
  pendingCreate: Array<number>,
|};

export type deleteTimerState = {|
  fetching: boolean,
  error: boolean,
  pendingRemoval: Array<number>,
|};

export type TimersState = {|
  content: Array<Timer>,
  error: boolean,
  fetching: boolean,
  loaded: boolean,
|};

// State
export type State = {|
  recordings: RecordingsState,
  timers: TimersState,
  scheduled: TimersState,
  recordingsDetails: {|
    content: Array<RecordingDetail>,
    error: boolean,
    fetching: boolean,
  |},
  deleteRecording: {|
    fetching: boolean,
    error: boolean,
  |},
  createTimer: createTimerState,
  updating: {|
    fetching: boolean,
    error: boolean,
    pendingUpdate: Array<number>,
    pendingUpdateData: Array<NewTimer>,
  |},
  deleteTimer: deleteTimerState,
  deletedTimers: Array<number>,
  deletedRecordings: Array<string>,
  skippingOrRestoring: {|
    fetching: boolean,
    error: boolean,
  |},
  setError: boolean,
  dvrUsageInPercents: ?number,
  dvrNumberOfRecordings: ?number,
  fetchingReceiverStatus: boolean,
  recordedAirings: Array<{ startDateTime: string, channel: number }>,
  dataGeneration: number,
  config: {
    recordingsViewMode: 'grid' | 'shelf',
    ptatViewMode: 'grid' | 'shelf',
    recordingsSort: string,
    ptatSort: string,
  },
|};

// Actions
export type Action =
  | {| type: 'REQUEST_RECORDINGS', filters: Filters |}
  | {| type: 'RECEIVE_RECORDINGS', payload: Array<Recording>, filters: Filters |}
  | {| type: 'REQUEST_RECORDINGS_ERROR', filters: Filters |}
  | {| type: 'REQUEST_RECORDINGS_DETAILS' |}
  | {| type: 'RECEIVE_RECORDINGS_DETAILS', payload: Array<RecordingDetail> |}
  | {| type: 'REQUEST_RECORDINGS_DETAILS_ERROR' |}
  | {| type: 'REQUEST_DELETE_RECORDING' |}
  | {| type: 'RECEIVE_DELETE_RECORDING', id: string, eventListing: ?DvrEventListing |}
  | {| type: 'REQUEST_DELETE_RECORDING_ERROR' |}
  | {| type: 'REQUEST_DELETE_MULTIPLE_RECORDINGS' |}
  | {| type: 'RECEIVE_DELETE_MULTIPLE_RECORDINGS' |}
  | {| type: 'REQUEST_DELETE_MULTIPLE_RECORDINGS_ERROR' |}
  | {| type: 'REQUEST_TIMERS' |}
  | {| type: 'RECEIVE_TIMERS', payload: Array<Timer> |}
  | {| type: 'REQUEST_TIMERS_ERROR' |}
  | {| type: 'REQUEST_CREATE_TIMER' |}
  | {|
      type: 'RECEIVE_CREATE_TIMER',
      payload: {|
        id: number,
        eventName: string,
        identifier: { startDateTime: string, channel: number },
        frequency: string,
      |},
    |}
  | {| type: 'REQUEST_CREATE_TIMER_ERROR', payload: Object |}
  | {| type: 'REQUEST_UPDATE_TIMER', payload: {| gTimerId: number, timer: NewTimer |} |}
  | {| type: 'RECEIVE_UPDATE_TIMER', payload: {| gTimerId: number, updatedTimer: NewTimer |} |}
  | {|
      type: 'REQUEST_UPDATE_TIMER_ERROR',
      payload: {| gTimerId: number, timer: NewTimer |},
    |}
  | {| type: 'REQUEST_DELETE_TIMER', payload: {| gTimerId: number |} |}
  | {| type: 'RECEIVE_DELETE_TIMER', payload: {| gTimerId: number |} |}
  | {| type: 'REQUEST_DELETE_TIMER_ERROR', payload: {| gTimerId: number |} |}
  | {| type: 'REQUEST_SKIP_RESTORE_TIMER' |}
  | {| type: 'RECEIVE_SKIP_RESTORE_TIMER_SUCCESS', timer: Timer |}
  | {| type: 'REQUEST_SKIP_RESTORE_TIMER_ERROR' |}
  | {| type: 'REQUEST_RECEIVER_INFO' |}
  | {| type: 'RECEIVE_RECEIVER_INFO', payload: ReceiverInfo |}
  | {| type: 'REQUEST_RECEIVER_INFO_ERROR' |}
  | {| type: 'REQUEST_RECEIVERS_STATUS' |}
  | {| type: 'RECEIVE_RECEIVERS_STATUS', payload: Array<ReceiverStatus> |}
  | {| type: 'REQUEST_RECEIVERS_STATUS_ERROR' |}
  | {| type: 'CHANGE_RECORDINGS_VIEW_MODE', viewMode: 'grid' | 'shelf' |}
  | {| type: 'CHANGE_PTAT_VIEW_MODE', viewMode: 'grid' | 'shelf' |}
  | {| type: 'CHANGE_RECORDINGS_SORT', sort: string |}
  | {| type: 'CHANGE_PTAT_SORT', sort: string |};
